// backendAI.ts — routes Gemini calls through the platform backend so the API key
// stays server-side and never ships in this browser bundle. The signed-in Firebase
// user's ID token authenticates the call; the backend verifies it and holds the key.

import { auth } from './firebase';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'https://a4idemo.pkfaudit.ai').replace(/\/$/, '');

function base64ToBlob(b64: string, mime: string): Blob {
  const byteChars = atob(b64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export interface BackendGenerateArgs {
  prompt: string;
  pdfBase64?: string | null;
  model?: string;
  temperature?: number;
  maxOutputTokens?: number;
  signal?: AbortSignal;
}

/**
 * Call the backend AI proxy. Returns a response shaped like the Gemini REST API
 * (`data.candidates[0].content.parts[0].text`) so existing parsers keep working.
 * Throws an Error whose message contains the HTTP status (e.g. "429") on failure.
 */
export async function backendGenerate({
  prompt,
  pdfBase64,
  model = 'gemini-2.5-flash',
  temperature = 0.2,
  maxOutputTokens = 8192,
  signal,
}: BackendGenerateArgs): Promise<any> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not signed in — please log in again to run analysis.');
  }
  const token = await user.getIdToken();

  const form = new FormData();
  form.append('prompt', prompt);
  form.append('model', model);
  form.append('temperature', String(temperature));
  form.append('max_output_tokens', String(maxOutputTokens));
  if (pdfBase64) {
    form.append('file', base64ToBlob(pdfBase64, 'application/pdf'), 'document.pdf');
  }

  const resp = await fetch(`${API_BASE}/api/v1/fsai/analyze`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
    signal,
  });

  if (!resp.ok) {
    let detail = '';
    try {
      const j = await resp.json();
      detail = j?.detail || '';
    } catch {
      /* ignore */
    }
    throw new Error(`Gemini API error: ${resp.status} ${detail}`.trim());
  }

  const json = await resp.json();
  const text = json?.text ?? '';
  return {
    candidates: [{ content: { parts: [{ text }] } }],
    usageMetadata: {
      promptTokenCount: json?.usage?.input ?? 0,
      candidatesTokenCount: json?.usage?.output ?? 0,
    },
    _model: json?.model,
  };
}

/**
 * Authenticated passthrough to Gemini generateContent via the backend. Pass the exact
 * request `body` you would have sent to Google (contents, generationConfig, system_instruction,
 * inline_data, etc.). Returns Gemini's response JSON verbatim (data.candidates[...]).
 * Throws on non-2xx (message includes the HTTP status, e.g. "429").
 */
export async function geminiProxy(model: string, body: any, signal?: AbortSignal): Promise<any> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not signed in — please log in again.');
  }
  const token = await user.getIdToken();

  const resp = await fetch(`${API_BASE}/api/v1/fsai/proxy`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ model, body }),
    signal,
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const detail = data?.error?.message || data?.detail || `${resp.status}`;
    throw new Error(`Gemini API error: ${resp.status} ${detail}`.trim());
  }
  return data;
}
